import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { resolveApiBase } from '@/utils/apiBase';
import {
  Activity,
  CheckCircle2,
  ChevronDown,
  Loader2,
  LogOut,
  MessageCircle,
  MonitorCog,
  Plus,
  Power,
  RefreshCw,
  Settings,
  Shield,
  Smartphone,
  Tag,
  ToggleLeft,
  ToggleRight,
  X,
  XCircle,
  Upload,
} from 'lucide-react';
import { Product, Role, ServiceCode, WhatsAppStatus, CvcrmStatusResponse } from '@/types';
import { fetchWhatsAppStatus as fetchWhatsAppStatusWithFallback } from '@/utils/whatsapp';
import { useServices } from '@/contexts/ServicesContext';
import { CvcrmConfigModal } from '../modals/CvcrmConfigModal';
import { ToastContainer, useToast } from '../ui/Toast';

const CORE_API = resolveApiBase(import.meta.env.VITE_CORE_API, '/core', 'http://localhost:3001');
const WHATSAPP_API = resolveApiBase(import.meta.env.VITE_WHATSAPP_API, '/whatsapp', 'http://localhost:3030');
const DEFAULT_BROWSER_API = resolveApiBase(import.meta.env.VITE_BROWSER_API, '/browser', 'http://localhost:3010');

const WhatsAppQRModal = React.lazy(() =>
  import('../modals/WhatsAppQRModal').then((mod) => ({ default: mod.WhatsAppQRModal }))
);

type Praca = {
  id: number;
  clienteId?: number;
  nome: string;
  slug: string;
  cidade?: string;
  estado?: string;
};

interface ConfiguracoesPageProps {
  darkMode: boolean;
  tenantName?: string;
  role: Role;
  whatsappApiBase?: string;
  browserApiBase?: string;
}

type SystemStatus = {
  healthy: boolean;
  tenantStatus: 'online' | 'offline' | 'partial';
  version: string;
  uptime: string;
  environment: string;
  servicesOnline: number;
  servicesTotal: number;
};

const formatUptime = (uptimeMs: number): string => {
  const seconds = Math.floor(uptimeMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h ${minutes % 60}m`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  return `${minutes}m`;
};

const serviceCodeToLabel: Record<ServiceCode, string> = {
  'RELATORIOS': 'Relatórios',
  'SDR': 'SDR',
  'GESTAO': 'Gestão',
  'COORDENACAO': 'Coordenação',
  'PESQUISA': 'Pesquisa',
};

export const ConfiguracoesPage: React.FC<ConfiguracoesPageProps> = ({ darkMode, tenantName, role, whatsappApiBase: externalWhatsappApiBase, browserApiBase: externalBrowserApiBase }) => {
  const { refreshServices } = useServices();
  const { toasts, toast, removeToast } = useToast();

  const [products, setProducts] = useState<Product[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const [browserApiBase, setBrowserApiBase] = useState(externalBrowserApiBase || DEFAULT_BROWSER_API);
  const [whatsappApiBase, setWhatsappApiBase] = useState(externalWhatsappApiBase || WHATSAPP_API);

  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [isCreatingProduct, setIsCreatingProduct] = useState(false);
  const [productForm, setProductForm] = useState<{
    nome: string;
    cidade: string;
    estado: string;
    fase: Product['fase'];
    pracaId: number | null;
  }>({
    nome: '',
    cidade: '',
    estado: '',
    fase: 'LANCAMENTO',
    pracaId: null,
  });

  const [pracas, setPracas] = useState<Praca[]>([]);
  const [isLoadingPracas, setIsLoadingPracas] = useState(true);
  const [isPracaModalOpen, setIsPracaModalOpen] = useState(false);
  const [isCreatingPraca, setIsCreatingPraca] = useState(false);
  const [pracaForm, setPracaForm] = useState<{ nome: string; slug: string }>({ nome: '', slug: '' });
  const pracaPorId = useMemo(() => {
    const map: Record<number, Praca> = {};
    pracas.forEach((p) => {
      if (p.id) map[p.id] = p;
    });
    return map;
  }, [pracas]);

  const [whatsappStatus, setWhatsappStatus] = useState<WhatsAppStatus>({
    status: 'disconnected',
    phoneNumber: null,
    messagesSent: 0,
    lastMessageAt: null,
    lastError: null,
  });
  const [isWhatsAppModalOpen, setIsWhatsAppModalOpen] = useState(false);
  const [isWhatsAppLoading, setIsWhatsAppLoading] = useState(false);

  const [cvcrmStatus, setCvcrmStatus] = useState<CvcrmStatusResponse | null>(null);
  const [isCvcrmModalOpen, setIsCvcrmModalOpen] = useState(false);

  const normalizeApiBase = useCallback((value: string | undefined, prodDefault: string) => {
    const raw = (value || '').trim();
    if (!raw) return prodDefault;
    if (raw.startsWith('http') || raw.startsWith('/')) return raw;
    return import.meta.env.PROD ? prodDefault : `http://localhost:${raw}`;
  }, []);

  useEffect(() => {
    if (externalBrowserApiBase) setBrowserApiBase(externalBrowserApiBase);
  }, [externalBrowserApiBase]);

  useEffect(() => {
    if (externalWhatsappApiBase) setWhatsappApiBase(externalWhatsappApiBase);
  }, [externalWhatsappApiBase]);
  const [isCvcrmDisconnecting, setIsCvcrmDisconnecting] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isSavingCategory, setIsSavingCategory] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ nome: '', cor: '#294b29' });
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [categorySuccess, setCategorySuccess] = useState<string | null>(null);
  const [isImobiliariaImportModalOpen, setIsImobiliariaImportModalOpen] = useState(false);
  const [isImportingImobiliarias, setIsImportingImobiliarias] = useState(false);
  const [imobiliariaImportForm, setImobiliariaImportForm] = useState<{ produtoId: string; file: File | null }>({
    produtoId: '',
    file: null,
  });
  const [imobiliariaImportIgnoreCnpj, setImobiliariaImportIgnoreCnpj] = useState(false);
  const [imobiliariaImportError, setImobiliariaImportError] = useState<string | null>(null);
  const [imobiliariaImportResult, setImobiliariaImportResult] = useState<{
    summary: { total: number; created: number; warnings: number; errors: number };
    items: Array<{ row: number; imobiliaria: string | null; status: string; messages: string[] }>;
  } | null>(null);

  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [tenantInfo, setTenantInfo] = useState<any | null>(null);
  const [managerError, setManagerError] = useState<string | null>(null);
  const lastCvcrmAuthStatusRef = useRef<string | null>(null);
  const isCvcrmLogged = cvcrmStatus?.auth?.status === 'LOGADO';
  const isCvcrmOtp = cvcrmStatus?.auth?.status === 'AGUARDANDO_OTP';
  const isCvcrmBrowserOnline = cvcrmStatus?.browser?.running === true;

  const resolveTenantId = () => {
    const clean = (value?: string | null) => (value && value.trim()) || '';
    const envTenant = clean(import.meta.env.VITE_TENANT_ID || import.meta.env.VITE_TENANT_NAME);
    if (envTenant) {
      if (typeof window !== 'undefined') {
        localStorage.setItem('va_tenant_id', envTenant);
        localStorage.setItem('va_tenant_name', envTenant);
      }
      return envTenant;
    }
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      const qsTenant = clean(url.searchParams.get('tenant') || url.searchParams.get('tenantId') || url.searchParams.get('t'));
      if (qsTenant) {
        localStorage.setItem('va_tenant_id', qsTenant);
        localStorage.setItem('va_tenant_name', qsTenant);
        localStorage.setItem('tenantId', qsTenant);
        return qsTenant;
      }
      const lsTenant =
        clean(localStorage.getItem('va_tenant_name')) ||
        clean(localStorage.getItem('va_tenant_id')) ||
        clean(localStorage.getItem('tenantId'));
      if (lsTenant) return lsTenant;
      const host = window.location.hostname;
      const maybeSub = host.split('.')[0];
      if (maybeSub && maybeSub.toLowerCase() !== 'localhost') {
        localStorage.setItem('va_tenant_id', maybeSub);
        localStorage.setItem('va_tenant_name', maybeSub);
        localStorage.setItem('tenantId', maybeSub);
        return maybeSub;
      }
    }
    const propTenant = clean(tenantName);
    if (propTenant && propTenant.toLowerCase() !== 'tenant') {
      if (typeof window !== 'undefined') {
        localStorage.setItem('va_tenant_id', propTenant);
        localStorage.setItem('va_tenant_name', propTenant);
        localStorage.setItem('tenantId', propTenant);
      }
      return propTenant;
    }
    return 'Tenant';
  };

  const [currentTenant, setCurrentTenant] = useState<string>(() => resolveTenantId());

  useEffect(() => {
    setCurrentTenant(resolveTenantId());
  }, [tenantName]);

  const getAuthHeaders = (): Record<string, string> => {
    const rawToken = (typeof window !== 'undefined' && localStorage.getItem('va_token')) || '';
    if (!rawToken) return {};
    return { Authorization: rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}` };
  };

  const resolveCoreApi = useCallback(async () => {
    const port = tenantInfo?.services?.core?.port;
    const coreApiRaw = (CORE_API || '').trim();
    if (import.meta.env.PROD) return coreApiRaw || '/core';
    if (!port || !coreApiRaw) return CORE_API;
    const baseCandidate =
      coreApiRaw ||
      (typeof window !== 'undefined' ? window.location.origin : '');
    const normalizedBase = baseCandidate.startsWith('http') || baseCandidate.startsWith('/')
      ? baseCandidate
      : `http://localhost:${baseCandidate}`;
    try {
      const url = new URL(normalizedBase);
      url.port = String(port);
      return url.origin;
    } catch {
      if (typeof window !== 'undefined' && window.location.origin) {
        try {
          const url = new URL(window.location.origin);
          url.port = String(port);
          return url.origin;
        } catch {
          return `http://localhost:${port}`;
        }
      }
      return `http://localhost:${port}`;
    }
  }, [tenantInfo]);

  const computeSystemStatus = useCallback((data: any) => {
    const services = Object.entries(data?.services || {});
    const onlineServices = services.filter(([, svc]: [string, any]) => svc.status === 'online');
    let oldestStartTime = Infinity;
    services.forEach(([, svc]: [string, any]) => {
      if (svc?.uptime && svc.uptime < oldestStartTime) oldestStartTime = svc.uptime;
    });
    const now = Date.now();
    const minValidTimestamp = new Date('2020-01-01').getTime();
    let realUptime = 0;
    if (oldestStartTime < now && oldestStartTime > minValidTimestamp) realUptime = now - oldestStartTime;
    setSystemStatus({
      healthy: data?.status === 'online' && onlineServices.length > 0,
      tenantStatus: data?.status || 'offline',
      version: 'v1.0.0-alpha',
      uptime: realUptime > 0 ? formatUptime(realUptime) : 'Ativo',
      environment: import.meta.env.PROD ? 'Produção' : 'Desenvolvimento',
      servicesOnline: onlineServices.length,
      servicesTotal: services.length,
    });
  }, []);

  const fetchTenantInfo = useCallback(async () => {
    try {
      setManagerError(null);
      const response = await fetch(`${CORE_API}/manager/tenants/${encodeURIComponent(currentTenant)}`, {
        headers: getAuthHeaders(),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const reason = payload?.error || payload?.message || `Falha ao buscar tenant: ${response.status}`;
        throw new Error(reason);
      }
      setTenantInfo(payload);
      computeSystemStatus(payload);
      const services = payload?.services || {};
      if (!externalBrowserApiBase && services?.browser?.port) {
        setBrowserApiBase(import.meta.env.PROD ? '/browser' : `http://localhost:${services.browser.port}`);
      }
      if (!externalWhatsappApiBase && services?.whatsapp?.port) {
        setWhatsappApiBase(import.meta.env.PROD ? '/whatsapp' : `http://localhost:${services.whatsapp.port}`);
      }
      return payload;
    } catch (err: any) {
      console.error('Erro ao buscar tenant no Manager (proxy):', err);
      setTenantInfo(null);
      setSystemStatus({
        healthy: false,
        tenantStatus: 'offline',
        version: 'v1.0.0-alpha',
        uptime: '-',
        environment: 'Desconhecido',
        servicesOnline: 0,
        servicesTotal: 0,
      });
      setManagerError(err?.message || 'Falha ao consultar o Manager.');
      return null;
    }
  }, [currentTenant, computeSystemStatus]);

  const createCategory = useCallback(async () => {
    setIsSavingCategory(true);
    setCategoryError(null);
    setCategorySuccess(null);
    try {
      const coreBase = await resolveCoreApi();
      const payload = {
        nome: categoryForm.nome.trim(),
        cor: categoryForm.cor || null,
      };
      if (!payload.nome) {
        setCategoryError('Informe um nome para a categoria.');
        return;
      }
      const response = await fetch(`${coreBase}/atendimentos/categorias`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const txt = await response.text();
        throw new Error(txt || 'Falha ao criar categoria');
      }
      setCategorySuccess('Categoria criada com sucesso!');
      setCategoryForm({ nome: '', cor: '#294b29' });
      toast.success('Categoria criada!');
    } catch (err: any) {
      console.error('Erro ao criar categoria:', err);
      setCategoryError(err?.message || 'Falha ao criar categoria.');
    } finally {
      setIsSavingCategory(false);
    }
  }, [categoryForm, resolveCoreApi]);

  const handleImobiliariaImport = useCallback(async () => {
    setImobiliariaImportError(null);
    setImobiliariaImportResult(null);
    if (!imobiliariaImportForm.produtoId) {
      setImobiliariaImportError('Selecione um produto.');
      return;
    }
    if (!imobiliariaImportForm.file) {
      setImobiliariaImportError('Selecione um arquivo CSV.');
      return;
    }
    setIsImportingImobiliarias(true);
    try {
      const coreBase = await resolveCoreApi();
      const formData = new FormData();
      formData.append('produtoId', imobiliariaImportForm.produtoId);
      formData.append('file', imobiliariaImportForm.file);
      formData.append('ignoreCnpjCheck', imobiliariaImportIgnoreCnpj ? '1' : '0');
      const response = await fetch(`${coreBase}/imobiliarias/import`, {
        method: 'POST',
        headers: { ...getAuthHeaders() },
        body: formData,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = payload?.error || payload?.message || 'Falha ao importar imobiliarias';
        throw new Error(message);
      }
      setImobiliariaImportResult(payload);
      toast.success('Importacao concluida');
    } catch (err: any) {
      console.error('Erro ao importar imobiliarias:', err);
      setImobiliariaImportError(err?.message || 'Falha ao importar imobiliarias.');
    } finally {
      setIsImportingImobiliarias(false);
    }
  }, [imobiliariaImportForm, imobiliariaImportIgnoreCnpj, resolveCoreApi, toast]);

  const fetchProducts = useCallback(async () => {
    try {
      const coreBase = await resolveCoreApi();
      const response = await fetch(`${coreBase}/produtos`, { headers: { ...getAuthHeaders() } });
      if (!response.ok) throw new Error('Falha ao carregar produtos');
      const data = await response.json();
      const apiProducts = data.produtos || [];
      setProducts(Array.isArray(apiProducts) ? apiProducts : []);
    } catch (err) {
      console.error('Erro ao buscar produtos:', err);
      setProducts([]);
    } finally {
      setIsLoadingProducts(false);
    }
  }, [resolveCoreApi]);

  const fetchPracas = useCallback(async () => {
    try {
      const coreBase = await resolveCoreApi();
      const response = await fetch(`${coreBase}/pracas`, { headers: { ...getAuthHeaders() } });
      if (!response.ok) throw new Error('Falha ao carregar praças');
      const data = await response.json();
      const apiPracas: Praca[] = data.pracas || data || [];
      setPracas(Array.isArray(apiPracas) ? apiPracas : []);
    } catch (err) {
      console.error('Erro ao buscar praças:', err);
      setPracas([]);
    } finally {
      setIsLoadingPracas(false);
    }
  }, [resolveCoreApi]);

  const fetchWhatsAppStatus = useCallback(async () => {
    try {
      const base = whatsappApiBase || WHATSAPP_API;
      const status = await fetchWhatsAppStatusWithFallback(base, getAuthHeaders());
      if (status) setWhatsappStatus(status);
    } catch (err) {
      console.error('Erro ao buscar status WhatsApp:', err);
    }
  }, [whatsappApiBase]);

  const fetchCvcrmStatus = useCallback(async (baseOverride?: string) => {
    const candidates = [baseOverride, browserApiBase, DEFAULT_BROWSER_API].filter(Boolean) as string[];
    const bases = candidates.filter((value, index) => candidates.indexOf(value) === index);
    for (const base of bases) {
      try {
        const normalized = base.startsWith('http') || base.startsWith('/')
          ? base
          : (import.meta.env.PROD ? '/browser' : `http://localhost:${base}`);
        const response = await fetch(`${normalized}/status?ts=${Date.now()}`, { cache: 'no-store' });
        if (response.ok) {
          const data: CvcrmStatusResponse = await response.json();
          setCvcrmStatus(data);
          return;
        }
      } catch (err) {
        console.error('Erro ao buscar status CVCRM:', err);
      }
    }
    setCvcrmStatus(null);
  }, [browserApiBase]);

  const resolveBrowserApi = useCallback((port?: number | null) => {
    const baseRaw = (browserApiBase || DEFAULT_BROWSER_API || '').trim();
    if (import.meta.env.PROD) return baseRaw || '/browser';
    if (!port || !baseRaw) return baseRaw || DEFAULT_BROWSER_API;
    const baseCandidate =
      baseRaw ||
      (typeof window !== 'undefined' ? window.location.origin : '');
    const normalizedBase = baseCandidate.startsWith('http') || baseCandidate.startsWith('/')
      ? baseCandidate
      : `http://localhost:${baseCandidate}`;
    try {
      const url = new URL(normalizedBase);
      url.port = String(port);
      return url.origin;
    } catch {
      if (typeof window !== 'undefined' && window.location.origin) {
        try {
          const url = new URL(window.location.origin);
          url.port = String(port);
          return url.origin;
        } catch {
          return `http://localhost:${port}`;
        }
      }
      return `http://localhost:${port}`;
    }
  }, [browserApiBase]);

  const refreshAllStatuses = useCallback(async () => {
    const tenantPayload = await fetchTenantInfo();
    const browserPort = tenantPayload?.services?.browser?.port;
    const browserBase = resolveBrowserApi(browserPort);
    await fetchWhatsAppStatus();
    await fetchCvcrmStatus(browserBase);
  }, [fetchTenantInfo, fetchWhatsAppStatus, fetchCvcrmStatus, resolveBrowserApi]);

  const handleCvcrmDisconnect = useCallback(async () => {
    if (!confirm('Tem certeza que deseja desconectar o CVCRM?')) return;
    setIsCvcrmDisconnecting(true);
    try {
      const response = await fetch(`${CORE_API}/manager/tenants/${encodeURIComponent(currentTenant)}/browser/logout`, {
        method: 'POST',
        headers: { ...getAuthHeaders() },
      });
      if (!response.ok) {
        const txt = await response.text();
        throw new Error(txt || 'Falha ao desconectar CVCRM');
      }
      await fetchCvcrmStatus();
      toast.success('CVCRM desconectado');
    } catch (err) {
      console.error('Erro ao desconectar CVCRM:', err);
      toast.error('Falha ao desconectar CVCRM');
    } finally {
      setIsCvcrmDisconnecting(false);
    }
  }, [currentTenant, fetchCvcrmStatus, toast]);

  useEffect(() => {
    refreshAllStatuses();
    const intervalAll = setInterval(refreshAllStatuses, 30000);
    return () => clearInterval(intervalAll);
  }, [currentTenant, refreshAllStatuses]);

  useEffect(() => {
    fetchProducts();
    fetchPracas();
  }, [fetchProducts, fetchPracas]);

  useEffect(() => {
    const currentStatus = cvcrmStatus?.auth?.status || null;
    if (currentStatus && currentStatus !== 'LOGADO' && currentStatus !== lastCvcrmAuthStatusRef.current) {
      toast.error('CVCRM desconectado (' + currentStatus + ').');
    }
    lastCvcrmAuthStatusRef.current = currentStatus;
  }, [cvcrmStatus?.auth?.status, toast]);

  const handleToggleService = async (productId: number, code: ServiceCode, currentValue: boolean) => {
    const actionKey = `${productId}-${code}`;
    setActionLoading(actionKey);
    const previousProducts = [...products];
    setProducts((prev) =>
      prev.map((p) =>
        p.id === productId
          ? {
              ...p,
              servicos: p.servicos?.map((s) => (s.codigo === code ? { ...s, habilitado: !currentValue } : s)),
            }
          : p
      )
    );
    try {
      const coreBase = await resolveCoreApi();
      const response = await fetch(`${coreBase}/produtos/${productId}/servicos/${code}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ habilitado: !currentValue }),
      });
      if (!response.ok) throw new Error('Falha ao atualizar serviço');
      await refreshServices();
      await fetchProducts();
    } catch (err) {
      console.error('Erro ao toggle serviço:', err);
      setProducts(previousProducts);
    } finally {
      setActionLoading(null);
    }
  };

  const slugify = (value: string) =>
    value
      .normalize('NFD')
      .replace(/[\\u0300-\\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

  return (
    <div className={`p-8 min-h-full ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="text-vision-green" />
            Configurações do Sistema
          </h1>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Gerencie infraestrutura, serviços e integrações.
          </p>
        </div>
        <button
          onClick={async () => {
            await fetchTenantInfo();
            fetchProducts();
            fetchPracas();
            fetchWhatsAppStatus();
            fetchCvcrmStatus();
            setLastUpdated(new Date());
          }}
          className={`text-xs flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors ${
            darkMode ? 'border-gray-700 hover:bg-gray-800' : 'border-gray-200 hover:bg-gray-50'
          }`}
        >
          <RefreshCw size={12} className={isLoadingProducts ? 'animate-spin' : ''} />
          {lastUpdated.toLocaleTimeString()}
        </button>
      </div>

      <section className="mb-8">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Activity size={20} /> Status do Sistema
        </h2>
        <div
          className={`p-6 rounded-xl border ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}
        >
          {!systemStatus ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="animate-spin text-gray-400" size={24} />
            </div>
          ) : (
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium">Tenant</p>
                <p className="text-xl font-bold">{currentTenant}</p>
                {managerError && <p className="text-xs text-red-400">{managerError}</p>}
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span
                  className={`px-3 py-1 rounded-full ${
                    systemStatus.healthy ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}
                >
                  {systemStatus.tenantStatus}
                </span>
                <span>
                  {systemStatus.servicesOnline}/{systemStatus.servicesTotal} serviços
                </span>
                <span>Uptime: {systemStatus.uptime}</span>
              </div>
            </div>
          )}
        </div>
      </section>

      {isProductModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div
            className={`w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden ${
              darkMode ? 'bg-vision-darkGreen text-white border border-vision-border' : 'bg-white text-gray-900'
            }`}
          >
            <div className={`flex items-center justify-between px-6 py-4 border-b ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}>
              <div>
                <h3 className="text-xl font-bold">Cadastro de Produto</h3>
              </div>
              <button
                onClick={() => !isCreatingProduct && setIsProductModalOpen(false)}
                className={`p-2 rounded-full transition-colors ${darkMode ? 'hover:bg-vision-surface' : 'hover:bg-gray-100'}`}
                aria-label="Fechar modal"
              >
                <X size={18} />
              </button>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setIsCreatingProduct(true);
                const payload = {
                  nome: productForm.nome,
                  cidade: productForm.cidade,
                  estado: productForm.estado,
                  fase: productForm.fase,
                  praca_id: productForm.pracaId,
                  ativo: true,
                };
                try {
                  const coreBase = await resolveCoreApi();
                  const response = await fetch(`${coreBase}/produtos`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                    body: JSON.stringify(payload),
                  });
                  if (!response.ok) {
                    const errText = await response.text().catch(() => '');
                    throw new Error(errText || `Falha ao cadastrar produto (${response.status})`);
                  }
                  const data = await response.json().catch(() => ({}));
                  const created = data?.produto || data;
                  if (created?.id) setProducts((prev) => [...prev, created]);
                  await fetchProducts();
                  toast.success('Produto cadastrado com sucesso.');
                  setIsProductModalOpen(false);
                  setProductForm({ nome: '', cidade: '', estado: '', fase: 'LANCAMENTO', pracaId: null });
                } catch (err) {
                  console.error('Erro ao criar produto', err);
                  toast.error('Não foi possível cadastrar o produto. Tente novamente.');
                } finally {
                  setIsCreatingProduct(false);
                }
              }}
              className="px-6 py-5 space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium ml-1">Nome do produto</label>
                  <input
                    required
                    value={productForm.nome}
                    onChange={(e) => setProductForm((prev) => ({ ...prev, nome: e.target.value }))}
                    className={`w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all ${
                      darkMode
                        ? 'bg-vision-surface border-vision-border text-white focus:ring-gray-600'
                        : 'bg-white border-gray-300 text-gray-900 focus:ring-[#294b29] focus:border-transparent'
                    }`}
                    placeholder="Ex: Residencial Alpha"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium ml-1">Cidade</label>
                  <input
                    required
                    value={productForm.cidade}
                    onChange={(e) => setProductForm((prev) => ({ ...prev, cidade: e.target.value }))}
                    className={`w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all ${
                      darkMode
                        ? 'bg-vision-surface border-vision-border text-white focus:ring-gray-600'
                        : 'bg-white border-gray-300 text-gray-900 focus:ring-[#294b29] focus:border-transparent'
                    }`}
                    placeholder="Ex: Fortaleza"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium ml-1">Estado (UF)</label>
                  <input
                    required
                    maxLength={2}
                    value={productForm.estado}
                    onChange={(e) => setProductForm((prev) => ({ ...prev, estado: e.target.value.toUpperCase() }))}
                    className={`w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all uppercase ${
                      darkMode
                        ? 'bg-vision-surface border-vision-border text-white focus:ring-gray-600'
                        : 'bg-white border-gray-300 text-gray-900 focus:ring-[#294b29] focus:border-transparent'
                    }`}
                    placeholder="PR"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium ml-1">Fase</label>
                  <div className="relative">
                    <select
                      value={productForm.fase}
                      onChange={(e) => setProductForm((prev) => ({ ...prev, fase: e.target.value as Product['fase'] }))}
                      className={`w-full px-4 py-2.5 pr-10 rounded-lg border outline-none focus:ring-2 transition-all appearance-none shadow-sm ${
                        darkMode
                          ? 'bg-vision-surface border-vision-border text-white focus:ring-vision-green/50 hover:border-vision-green/60'
                          : 'bg-white border-[#294b29]/50 text-gray-900 focus:ring-[#294b29] focus:border-[#294b29] hover:border-[#294b29]'
                      }`}
                    >
                      <option value="PRE_LANCAMENTO">Pré-lançamento</option>
                      <option value="LANCAMENTO">Lançamento</option>
                      <option value="EM_VENDAS">Estoque</option>
                      <option value="FINALIZADO">Finalizado</option>
                    </select>
                    <ChevronDown
                      size={18}
                      className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 ${
                        darkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium ml-1">Praça</label>
                  <div className="relative">
                    <select
                      value={productForm.pracaId ?? ''}
                      required
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '__new') {
                          setIsPracaModalOpen(true);
                          setProductForm((prev) => ({ ...prev, pracaId: null }));
                          return;
                        }
                        setProductForm((prev) => ({ ...prev, pracaId: val ? Number(val) : null }));
                      }}
                      className={`w-full px-4 py-2.5 pr-10 rounded-lg border outline-none focus:ring-2 transition-all appearance-none shadow-sm ${
                        darkMode
                          ? 'bg-vision-surface border-vision-border text-white focus:ring-vision-green/50 hover:border-vision-green/60'
                          : 'bg-white border-[#294b29]/50 text-gray-900 focus:ring-[#294b29] focus:border-[#294b29] hover:border-[#294b29]'
                      }`}
                      disabled={isLoadingPracas}
                    >
                      <option value="">Selecione a praça</option>
                      {pracas.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.nome} {p.estado ? `(${p.estado})` : ''}
                        </option>
                      ))}
                      <option value="__new">+ Adicionar nova praça</option>
                    </select>
                    <ChevronDown
                      size={18}
                      className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 ${
                        darkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    />
                  </div>
                  {isLoadingPracas && <p className="text-xs text-gray-400">Carregando praças...</p>}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => !isCreatingProduct && setIsProductModalOpen(false)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    darkMode ? 'border-vision-border hover:bg-vision-surface' : 'border-gray-200 hover:bg-gray-100'
                  }`}
                  disabled={isCreatingProduct}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isCreatingProduct}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all active:scale-95 shadow ${
                    darkMode ? 'bg-vision-activeRed hover:bg-red-700' : 'bg-[#294b29] hover:bg-[#1a301a]'
                  } ${isCreatingProduct ? 'opacity-80 cursor-wait' : ''}`}
                >
                  {isCreatingProduct ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                  {isCreatingProduct ? 'Salvando...' : 'Salvar produto'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isPracaModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div
            className={`w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden ${
              darkMode ? 'bg-vision-darkGreen text-white border border-vision-border' : 'bg-white text-gray-900'
            }`}
          >
            <div className={`flex items-center justify-between px-6 py-4 border-b ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}>
              <div>
                <h3 className="text-xl font-bold">Cadastro de praça</h3>
              </div>
              <button
                onClick={() => !isCreatingPraca && setIsPracaModalOpen(false)}
                className={`p-2 rounded-full transition-colors ${darkMode ? 'hover:bg-vision-surface' : 'hover:bg-gray-100'}`}
                aria-label="Fechar modal"
              >
                <X size={18} />
              </button>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setIsCreatingPraca(true);
                const normalizedSlug = pracaForm.slug || slugify(pracaForm.nome);
                const payload = { nome: pracaForm.nome, slug: normalizedSlug };
                try {
                  const coreBase = await resolveCoreApi();
                  const response = await fetch(`${coreBase}/pracas`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                    body: JSON.stringify(payload),
                  });
                  if (!response.ok) {
                    const errText = await response.text().catch(() => '');
                    console.error('Erro ao criar praça', { status: response.status, statusText: response.statusText, payload, errText });
                    throw new Error(errText || 'Falha ao cadastrar praça');
                  }
                  const data = await response.json().catch(() => ({}));
                  const created: Praca = data?.praca || data || { id: Date.now(), ...payload };
                  setPracas((prev) => [...prev, created]);
                  setProductForm((prev) => ({ ...prev, pracaId: created.id }));
                  toast.success('Praça cadastrada com sucesso.');
                  setIsPracaModalOpen(false);
                  setPracaForm({ nome: '', slug: '' });
                } catch (err) {
                  console.error(err);
                  toast.error('Não foi possível cadastrar a praça. Tente novamente.');
                } finally {
                  setIsCreatingPraca(false);
                }
              }}
              className="px-6 py-5 space-y-4"
            >
              <div className="space-y-2">
                <label className="text-sm font-medium ml-1">Nome da praça</label>
                <input
                  required
                  value={pracaForm.nome}
                  onChange={(e) => {
                    const nome = e.target.value;
                    setPracaForm((prev) => ({ ...prev, nome, slug: prev.slug || slugify(nome) }));
                  }}
                  className={`w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all ${
                    darkMode
                      ? 'bg-vision-surface border-vision-border text-white focus:ring-gray-600'
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-[#294b29] focus:border-transparent'
                  }`}
                  placeholder="Ex: Ceará"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium ml-1">Slug</label>
                <input
                  required
                  value={pracaForm.slug}
                  onChange={(e) => setPracaForm((prev) => ({ ...prev, slug: slugify(e.target.value) }))}
                  className={`w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all font-mono text-sm ${
                    darkMode
                      ? 'bg-vision-surface border-vision-border text-white focus:ring-gray-600'
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-[#294b29] focus:border-transparent'
                  }`}
                  placeholder="ceara"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => !isCreatingPraca && setIsPracaModalOpen(false)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    darkMode ? 'border-vision-border hover:bg-vision-surface' : 'border-gray-200 hover:bg-gray-100'
                  }`}
                  disabled={isCreatingPraca}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isCreatingPraca}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all active:scale-95 shadow ${
                    darkMode ? 'bg-vision-activeRed hover:bg-red-700' : 'bg-[#294b29] hover:bg-[#1a301a]'
                  } ${isCreatingPraca ? 'opacity-80 cursor-wait' : ''}`}
                >
                  {isCreatingPraca ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                  {isCreatingPraca ? 'Salvando...' : 'Salvar praça'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">Produtos & Serviços</h2>
          {role === 'admin' && (
            <button
              onClick={() => setIsProductModalOpen(true)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors shadow-sm ${
                darkMode ? 'bg-vision-activeRed text-white hover:bg-red-700' : 'bg-[#294b29] text-white hover:bg-[#1a301a]'
              }`}
            >
              <Plus size={16} />
              Cadastrar produto
            </button>
          )}
        </div>

        {isLoadingProducts ? (
          <div className="flex items-center justify-center p-12">
            <Loader2 className="animate-spin text-vision-green" size={32} />
          </div>
        ) : products.length === 0 ? (
          <div
            className={`p-8 text-center rounded-xl border ${
              darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-gray-50 border-gray-200'
            }`}
          >
            <Power size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-gray-500">Nenhum produto cadastrado</p>
            <p className="text-sm text-gray-400 mt-1">Cadastre produtos na página de Produtos</p>
          </div>
        ) : (
          <div
            className={`rounded-xl border overflow-hidden ${
              darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'
            }`}
          >
            {products.map((prod, index) => (
              <div
                key={prod.id}
                className={`p-6 ${index !== products.length - 1 ? 'border-b border-gray-100 dark:border-gray-800' : ''}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-8 w-8 rounded bg-gray-100 dark:bg-gray-800 flex items-center justify-center font-bold text-gray-500">
                    {prod.nome.substring(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">{prod.nome}</h3>
                    <p className="text-xs text-gray-500">
                      {prod.cidade} / {prod.estado} |{' '}
                      {(() => {
                        const pracaId = (prod as any).praca_id ?? (prod as any).pracaId ?? null;
                        const praca = pracaId ? pracaPorId[pracaId] : null;
                        return praca?.nome || 'Praca nao vinculada';
                      })()}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                  {prod.servicos?.map((srv) => {
                    const isLoading = actionLoading === `${prod.id}-${srv.codigo}`;
                    return (
                      <button
                        key={srv.codigo}
                        onClick={() => handleToggleService(prod.id, srv.codigo as ServiceCode, srv.habilitado)}
                        disabled={isLoading || actionLoading !== null}
                        className={`flex items-center justify-between p-3 rounded-lg border transition-all disabled:opacity-50 ${
                          srv.habilitado
                            ? darkMode
                              ? 'bg-vision-green/10 border-green-800 text-green-300 shadow-[0_0_12px_rgba(20,83,45,0.4)] ring-1 ring-green-800/35'
                              : 'bg-vision-green/10 border-vision-green text-vision-green'
                            : darkMode
                            ? 'bg-gray-900 border-gray-800 text-gray-500'
                            : 'bg-gray-50 border-gray-200 text-gray-400'
                        }`}
                      >
                        <span className="text-sm font-medium">{serviceCodeToLabel[srv.codigo]}</span>
                        {isLoading ? (
                          <Loader2 size={20} className="animate-spin" />
                        ) : srv.habilitado ? (
                          <ToggleRight size={24} />
                        ) : (
                          <ToggleLeft size={24} />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={`p-6 rounded-xl border ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${darkMode ? 'bg-vision-surface text-white' : 'bg-gray-100 text-gray-700'}`}>
              <Upload size={24} />
            </div>
            <div className="flex-1 min-w-0 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className={`font-bold text-lg ${darkMode ? 'text-white' : 'text-gray-800'}`}>Add & Imports</h3>
              </div>
              {categoryError && <p className="text-sm text-red-500">{categoryError}</p>}
              {categorySuccess && <p className="text-sm text-green-600">{categorySuccess}</p>}
              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={() => { setIsCategoryModalOpen(true); setCategoryError(null); setCategorySuccess(null); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-all active:scale-95 shadow-md ${darkMode ? 'bg-blue-700 hover:bg-blue-600' : 'bg-[#294b29] hover:bg-[#1a301a]'}`}
                >
                  <Plus size={16} />
                  Categorias de atendimentos
                </button>
                <button
                  onClick={() => {
                    setIsImobiliariaImportModalOpen(true);
                    setImobiliariaImportError(null);
                    setImobiliariaImportResult(null);
                    setImobiliariaImportForm((prev) => ({ ...prev, file: null }));
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-all active:scale-95 shadow-md ${darkMode ? 'bg-blue-700 hover:bg-blue-600' : 'bg-[#294b29] hover:bg-[#1a301a]'}`}
                >
                  <Plus size={16} />
                  Imobiliarias
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className={`p-6 rounded-xl border ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
          <div className="flex items-start gap-4">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${
                cvcrmStatus?.auth?.status === 'LOGADO'
                  ? 'bg-green-100 text-green-600'
                  : cvcrmStatus?.auth?.status === 'AGUARDANDO_OTP'
                  ? 'bg-yellow-100 text-yellow-600'
                  : cvcrmStatus?.browser?.running
                  ? 'bg-blue-100 text-blue-600'
                  : 'bg-gray-100 text-gray-400'
              }`}
            >
              {isCvcrmLogged ? (
                <CheckCircle2 size={24} />
              ) : isCvcrmOtp ? (
                <MonitorCog size={24} />
              ) : isCvcrmBrowserOnline ? (
                <MonitorCog size={24} />
              ) : (
                <XCircle size={24} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <h3
                  className={`font-bold text-lg ${
                    isCvcrmLogged
                      ? 'text-green-600'
                      : isCvcrmOtp
                      ? 'text-yellow-600'
                      : 'text-gray-500'
                  }`}
                >
                  CVCRM
                </h3>
                <span
                  className={`text-xs font-mono px-2 py-0.5 rounded ${
                    isCvcrmBrowserOnline
                      ? isCvcrmLogged
                        ? 'bg-green-100 text-green-800'
                        : 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {isCvcrmBrowserOnline ? 'online' : 'offline'}
                </span>
              </div>
              {isCvcrmLogged ? (
                <>
                  <p className={`text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {cvcrmStatus.auth?.usuario || 'Usuário conectado'}
                  </p>
                  <p className={`text-xs mb-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {cvcrmStatus.auth?.cargo || 'Gestor'}
                  </p>
                </>
              ) : isCvcrmOtp ? (
                <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Digite o código OTP para completar o login.
                </p>
              ) : isCvcrmBrowserOnline ? (
                <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Browser ativo, mas não está logado no CVCRM.
                </p>
              ) : (
                <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Faça login no CVCRM para continuar.
                </p>
              )}

              <div className="flex gap-3 flex-wrap">
                {isCvcrmLogged ? (
                  <button
                    onClick={handleCvcrmDisconnect}
                    disabled={isCvcrmDisconnecting}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-all active:scale-95 shadow-md ${
                      darkMode ? 'bg-red-700 hover:bg-red-600' : 'bg-red-600 hover:bg-red-700'
                    } ${isCvcrmDisconnecting ? 'opacity-70 cursor-not-allowed' : ''}`}
                  >
                    {isCvcrmDisconnecting ? <Loader2 size={16} className="animate-spin" /> : <LogOut size={16} />}
                    Desconectar
                  </button>
                ) : (
                  <button
                    onClick={() => setIsCvcrmModalOpen(true)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-all active:scale-95 shadow-md ${
                      darkMode ? 'bg-blue-700 hover:bg-blue-600' : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    <Shield size={16} />
                    Conectar
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={`p-6 rounded-xl border ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}`}>
          <div className="flex items-start gap-4">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${
                whatsappStatus.status === 'ready' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
              }`}
            >
              <MessageCircle size={24} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <h3 className={`font-bold text-lg ${whatsappStatus.status === 'ready' ? 'text-green-600' : 'text-gray-500'}`}>
                  {whatsappStatus.status === 'ready' ? 'WhatsApp Conectado' : 'WhatsApp Desconectado'}
                </h3>
              </div>
              {whatsappStatus.status === 'ready' ? (
        <>
          <p className={`text-sm font-mono mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {whatsappStatus.phoneNumber}
          </p>
          <p className={`text-xs mb-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {whatsappStatus.messagesSent} mensagens enviadas hoje.
          </p>
          <button
            onClick={async () => {
              if (!confirm('Tem certeza que deseja desconectar o WhatsApp?')) return;
              setIsWhatsAppLoading(true);
              try {
                const base = whatsappApiBase || WHATSAPP_API;
                const normalized = normalizeApiBase(base, '/whatsapp');
                const res = await fetch(`${normalized}/disconnect`, { method: 'POST', headers: { ...getAuthHeaders() } });
                if (!res.ok) {
                  const txt = await res.text();
                  throw new Error(txt || 'Falha ao desconectar');
                }
                await fetchWhatsAppStatus();
                toast.success('WhatsApp desconectado');
              } catch (err) {
                console.error('Erro ao desconectar WhatsApp:', err);
                toast.error('Falha ao desconectar WhatsApp');
              } finally {
                setIsWhatsAppLoading(false);
              }
            }}
            disabled={isWhatsAppLoading}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border border-red-200 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
          >
                    {isWhatsAppLoading ? <Loader2 size={14} className="animate-spin" /> : <LogOut size={14} />}
                    Desconectar
                  </button>
                </>
              ) : (
                <>
                  <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Conecte seu aparelho para enviar mensagens automáticas.
                  </p>
                  <button
                    onClick={() => setIsWhatsAppModalOpen(true)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-all active:scale-95 shadow-md ${
                      darkMode ? 'bg-green-700 hover:bg-green-600' : 'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    <Smartphone size={16} />
                    Conectar WhatsApp
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <React.Suspense fallback={null}>
        <WhatsAppQRModal
          isOpen={isWhatsAppModalOpen}
          onClose={() => setIsWhatsAppModalOpen(false)}
          darkMode={darkMode}
          apiBase={whatsappApiBase}
          headers={getAuthHeaders()}
          onSuccess={() => {
            fetchWhatsAppStatus();
          }}
        />
      </React.Suspense>

      <CvcrmConfigModal
        isOpen={isCvcrmModalOpen}
        onClose={() => setIsCvcrmModalOpen(false)}
        darkMode={darkMode}
        currentStatus={cvcrmStatus?.auth?.status || null}
        tenantName={tenantName}
        browserApiBase={browserApiBase}
        onSuccess={() => {
          fetchCvcrmStatus();
          toast.success('CVCRM conectado com sucesso!');
        }}
      />

      {isCategoryModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className={`w-full max-w-md rounded-xl border shadow-xl ${darkMode ? 'bg-vision-darkGreen border-vision-border text-white' : 'bg-white border-gray-200 text-gray-800'}`}>
            <div className="p-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Tag size={18} />
                <h4 className="font-bold text-lg">Nova categoria de chamado</h4>
              </div>
              <button onClick={() => setIsCategoryModalOpen(false)} className={`${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}>
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-semibold">Nome</label>
                <input
                  type="text"
                  value={categoryForm.nome}
                  onChange={(e) => setCategoryForm({ ...categoryForm, nome: e.target.value })}
                  className={`w-full px-3 py-2 rounded border ${darkMode ? 'bg-vision-surface border-vision-border text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                  placeholder="Ex: Dúvida sobre boleto"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold">Cor (opcional)</label>
                <input
                  type="color"
                  value={categoryForm.cor}
                  onChange={(e) => setCategoryForm({ ...categoryForm, cor: e.target.value })}
                  className="w-16 h-10 p-1 rounded border border-gray-300"
                />
              </div>
              {categoryError && <p className="text-sm text-red-500">{categoryError}</p>}
              {categorySuccess && <p className="text-sm text-green-600">{categorySuccess}</p>}
            </div>
            <div className={`p-4 border-t flex justify-end gap-3 ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}>
              <button
                onClick={() => setIsCategoryModalOpen(false)}
                className={`${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              >
                Cancelar
              </button>
              <button
                onClick={createCategory}
                disabled={isSavingCategory}
                className={`px-4 py-2 rounded-lg text-white font-semibold transition-all ${isSavingCategory ? 'bg-gray-400 cursor-not-allowed' : darkMode ? 'bg-blue-700 hover:bg-blue-600' : 'bg-[#294b29] hover:bg-[#1a301a]'}`}
              >
                {isSavingCategory ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isImobiliariaImportModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className={`w-full max-w-2xl rounded-xl border shadow-xl ${darkMode ? 'bg-vision-darkGreen border-vision-border text-white' : 'bg-white border-gray-200 text-gray-800'}`}>
            <div className="p-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Plus size={18} />
                <h4 className="font-bold text-lg">Importar imobiliarias (CSV)</h4>
              </div>
              <button onClick={() => setIsImobiliariaImportModalOpen(false)} className={`${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}>
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-semibold">Produto</label>
                <select
                  value={imobiliariaImportForm.produtoId}
                  onChange={(e) => {
                    setImobiliariaImportForm((prev) => ({ ...prev, produtoId: e.target.value }));
                    setImobiliariaImportError(null);
                    setImobiliariaImportResult(null);
                  }}
                  className={`w-full px-3 py-2 rounded border ${darkMode ? 'bg-vision-surface border-vision-border text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                >
                  <option value="">{isLoadingProducts ? 'Carregando produtos...' : 'Selecione um produto'}</option>
                  {products.map((product) => (
                    <option key={product.id} value={String(product.id)}>
                      {product.nome}
                    </option>
                  ))}
                </select>
                {!products.length && !isLoadingProducts && (
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Nenhum produto cadastrado. Cadastre um produto antes de importar imobiliarias.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold">Arquivo CSV</label>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setImobiliariaImportForm((prev) => ({ ...prev, file }));
                    setImobiliariaImportError(null);
                    setImobiliariaImportResult(null);
                  }}
                  className={`w-full px-3 py-2 rounded border ${darkMode ? 'bg-vision-surface border-vision-border text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                />
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  CSV com cabecalho. Colunas esperadas (ordem): Imobiliaria;Razao social;Socio nome;Socio whatsapp;Gestor nome;Gestor whatsapp;CNPJ;CRECIPJ;Status. Obrigatorios: Imobiliaria, Razao social, Socio nome, Socio whatsapp, CNPJ (a menos que marque a opcao abaixo). Status opcional (padrao: Analise). Delimitador: ; ou ,.
                </p>
              </div>
              <div className="space-y-1">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={imobiliariaImportIgnoreCnpj}
                    onChange={(e) => setImobiliariaImportIgnoreCnpj(e.target.checked)}
                    className="h-4 w-4"
                  />
                  Importar sem validar CNPJ (pode duplicar imobiliarias)
                </label>
              </div>

              {imobiliariaImportError && <p className="text-sm text-red-500">{imobiliariaImportError}</p>}

              {imobiliariaImportResult && (
                <div className={`rounded-lg border p-3 text-sm ${darkMode ? 'border-vision-border bg-vision-surface/60' : 'border-gray-200 bg-gray-50'}`}>
                  <p className="font-semibold mb-1">Resumo</p>
                  <p>
                    Total: {imobiliariaImportResult.summary.total} | Criadas: {imobiliariaImportResult.summary.created} | Alertas: {imobiliariaImportResult.summary.warnings} | Erros: {imobiliariaImportResult.summary.errors}
                  </p>
                  {imobiliariaImportResult.items?.some((item) => item.status !== 'created') && (
                    <div className="mt-2">
                      <p className="text-xs font-semibold">Linhas com alerta/erro (ate 5)</p>
                      <ul className="text-xs space-y-1">
                        {imobiliariaImportResult.items
                          .filter((item) => item.status !== 'created')
                          .slice(0, 5)
                          .map((item) => (
                            <li key={`${item.row}-${item.imobiliaria || 'sem-nome'}`}>
                              #{item.row} {item.imobiliaria || 'Imobiliaria sem nome'} - {item.messages?.join(', ') || 'erro'}
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className={`p-4 border-t flex justify-end gap-3 ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}>
              <button
                onClick={() => setIsImobiliariaImportModalOpen(false)}
                className={`${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              >
                Cancelar
              </button>
              <button
                onClick={handleImobiliariaImport}
                disabled={isImportingImobiliarias}
                className={`px-4 py-2 rounded-lg text-white font-semibold transition-all ${isImportingImobiliarias ? 'bg-gray-400 cursor-not-allowed' : darkMode ? 'bg-blue-700 hover:bg-blue-600' : 'bg-[#294b29] hover:bg-[#1a301a]'}`}
              >
                {isImportingImobiliarias ? 'Importando...' : 'Importar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
};






